import { isEqual } from "lodash";
import { useEffect, useMemo, useState } from "react";

import { useDebounce } from "@nens/react/hooks";

import { JobStatusOrCreating } from "@/types/job-status-or-creating";

import { ProcessRunProps } from "../types/process-props";

// useIndex Manages which of the three accordion panels (form, status, results) are open.
// The default open/closed state is driven by the current job status — e.g. the form
// is open before a process starts, the status panel is open while running, and the results
// panel opens once the job completes. Users can override any panel manually, and those
// overrides are reset whenever a new job is started.

// And auto scroll to bottom is on whenever there is a status (so when creating, running, finished anything),
// but only if the user has not manually overridden the panel states.

// This all ensures that when a user starts a process, they are guided through the panels in a logical way,
// but they can also explore and control the panels as they wish without interference from the automatic behavior.

type PanelOverride = boolean | undefined;
type Overrides = [PanelOverride, PanelOverride, PanelOverride];

const EMPTY_OVERRIDES: Overrides = [undefined, undefined, undefined];

export const useIndex = (props: ProcessRunProps, status: JobStatusOrCreating) => {
  const { job } = props;
  const [overrides, setOverrides] = useState<Overrides>(EMPTY_OVERRIDES);

  // Reset user overrides when a new job is loaded
  useEffect(() => {
    setOverrides((overrides) => {
      if (isEqual(overrides, EMPTY_OVERRIDES)) return overrides;
      return EMPTY_OVERRIDES;
    });
  }, [job?.id]);

  // Delay opening the results panel when the job first transitions to completed
  const resultsAvailable = useDebounce(status === "completed", 2000);

  // Which panels should be open by default for each status
  const defaultOpen = useMemo((): [boolean, boolean, boolean] => {
    switch (status) {
      case undefined:
        return [true, false, false];
      case "creating":
        return [true, true, false];
      case "completed":
        return [false, !resultsAvailable, resultsAvailable];
      default:
        return [false, true, false];
    }
  }, [status, resultsAvailable]);

  // Merge user overrides with status-driven defaults
  const index = useMemo(
    () => ([0, 1, 2] as const).filter((i) => overrides[i] ?? defaultOpen[i]) as number[],
    [overrides, defaultOpen]
  );

  // True if the user has explicitly overridden at least one panel against its default
  const userSetIndex = overrides.some(
    (override, i) => override !== undefined && override !== defaultOpen[i]
  );

  return {
    index,
    userSetIndex,
    toggleTopOpen: () => setOverrides(([, m, b]) => [!index.includes(0), m, b]),
    toggleMiddleOpen: () => setOverrides(([t, , b]) => [t, !index.includes(1), b]),
    toggleBottomOpen: () => setOverrides(([t, m]) => [t, m, !index.includes(2)]),
  };
};
